import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";

import { isBrowser } from "~/utils/helpers";
import { Duration } from "~/constants";
import { useDocumentEvent } from "~/hooks/useDocumentEvent";
import { useUnmountEffect } from "~/hooks/useUnmountEffect";

import { DialogModalBackdrop } from "./DialogModalBackdrop";
import { DialogModalPanel } from "./DialogModalPanel";
import { DialogModalHeader } from "./DialogModalHeader";
import { DialogModalContainer } from "./DialogModalContainer";
import { DialogModalCoordinator } from "./DialogModalCoordinator";
import { DialogModalBody } from "./DialogModalBody";

import "./DialogModal.css";

import type { SizeProp } from "../components";

export interface DialogModalProps {
  children?: React.ReactNode;
  header?:
    | React.ReactNode
    | ((ctx: DialogModalContextValue) => React.ReactNode);
  isOpen?: boolean;
  size?: Extract<SizeProp, "sm" | "md" | "lg">;
  style?: React.CSSProperties;
  onRequestClose?: () => void;
  onUnmount?: () => void;
}

export interface DialogModalContextValue {
  isOpen?: boolean;
  requestClose?: () => void;
}

export const DIALOG_MODAL_ANIMATION_DURATION = Duration.Normal / 1_000;

export const DialogModalContext = createContext<DialogModalContextValue>({});

export function useDialogModal() {
  return useContext(DialogModalContext);
}

export function DialogModal({
  children,
  header,
  isOpen,
  size,
  style,
  onRequestClose,
  onUnmount,
}: DialogModalProps) {
  const id = useId();

  const [mounted, setMounted] = useState(isOpen);

  const requestClose = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  const handleExitComplete = useCallback(() => {
    setMounted(false);
    onUnmount?.();
  }, [onUnmount]);

  const ctx = useMemo<DialogModalContextValue>(
    () => ({
      isOpen,
      requestClose,
    }),
    [isOpen, requestClose],
  );

  const renderHeader = useCallback(() => {
    if (header === undefined) return <DialogModalHeader />;

    if (typeof header === "function") {
      return header(ctx);
    }

    return header;
  }, [ctx, header]);

  // Register keyboard handlers (a11y)
  useDocumentEvent(
    "keydown",
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          requestClose();
          break;
        default:
          break;
      }
    },
    true,
  );

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      DialogModalCoordinator.shared.add(id);
    } else {
      DialogModalCoordinator.shared.remove(id);
    }
  }, [id, isOpen]);

  useUnmountEffect(() => {
    DialogModalCoordinator.shared.remove(id);
  });

  // Only render in the browser (portals are browser only).
  // Don't render when closed and animations are complete.
  if (!isBrowser() || !mounted) return null;

  return createPortal(
    <DialogModalContext.Provider value={ctx}>
      <div
        className="DialogModal"
        role="dialog"
        aria-modal="true"
        data-size={size}
        style={style}
      >
        <AnimatePresence onExitComplete={handleExitComplete}>
          {isOpen && (
            <>
              <DialogModalBackdrop key="backdrop" />

              <DialogModalContainer key="content">
                <DialogModalPanel>
                  {renderHeader()}

                  {children}
                </DialogModalPanel>
              </DialogModalContainer>
            </>
          )}
        </AnimatePresence>
      </div>
    </DialogModalContext.Provider>,
    globalThis.document.body,
  );
}

DialogModal.Body = DialogModalBody;
